// The draft room's panes (DRAFT_ROOM.md, PLAN-2026-10-14 §3): the players with
// what a drafter reads about each, the board, every team's positions, the
// tiers left, the queue and my team. Each draws a `DraftView` and says
// nothing about who to take — numbers, a count and a line, never a sentence
// telling anybody what to do (docs/decisions/2026-09-12-tools-not-instructions.md).
//
// THE SAME PANES FOR EVERY ROOM: the practice league's, a real league's and a
// mock's. Where the view comes from is the screen's business (`DraftRoom.tsx`).

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { DetailLevel } from '@fsp/theme';
import type { DraftPlayerRow, DraftView } from '@fsp/domain';
import { OPEN_SEAT } from '@fsp/draft-room';
import { EmptyState, PositionPill, StatusBadge, TeamMark, positionLabel } from '../../kit';
import { TableScroll } from '../../kit/TableScroll';
import {
  Button,
  Caption,
  Chip,
  Chips,
  Colored,
  IconButton,
  Line,
  Segments,
  Text,
  TextField,
} from '../../kit/ui';
import { PlayerName } from '../shared/Names';
import { Term } from '../../kit/Why';
import { SortHead, sortRows, type SortFirst } from '../../kit/SortHead';
import { SeatButton, SeatName } from './DraftCards';
import {
  pickLabel,
  picksUntilMine,
  positionCounts,
  recentRun,
  sharedByes,
  teamsBeforeMine,
  tiersLeft,
} from './research';
import { clubLabel, clubMatches } from '../../sports/registry';

/** How a pick was made, in a word a cell has room for. A person's own pick needs none. */
export function byWord(by: DraftView['board'][number]['by']): string | null {
  if (by === 'ai') return 'bot';
  if (by === 'queue') return 'queue';
  if (by === 'plan') return 'priorities';
  if (by === 'rankings') return 'auto';
  return null;
}

const one = (n: number) => (Math.round(n * 10) / 10).toFixed(1);

// ── Players ─────────────────────────────────────────────────────────────────

type Sort = 'rank' | 'perGame' | 'total' | 'value' | 'bye' | 'name';

/** How many rows a list draws at once; a search reaches the rest. */
const SHOWN = 200;

export interface PlayersPaneProps {
  view: DraftView;
  positions: readonly string[];
  detail: DetailLevel;
  tier: 'phone' | 'tablet' | 'desktop' | 'large';
  /** The team a press of Draft picks for: mine on my turn, or the clock's for a commissioner picking for it. */
  pickingFor: { teamId: string; name: string; mine: boolean } | null;
  onPick: (playerId: string) => void;
  onToggleQueue: (playerId: string) => void;
  /**
   * An auction's: the press puts him up, and the opening bid it asks for is
   * the second look a pick's Confirm is (AUCTION.md §10).
   */
  nominate?: boolean;
}

export function PlayersPane({
  view,
  positions,
  detail,
  tier,
  pickingFor,
  onPick,
  onToggleQueue,
  nominate = false,
}: PlayersPaneProps) {
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<string>('all');
  const [sort, setSortKey] = useState<Sort>('rank');
  // TURNED ROUND by a second press on its column's head (B-377); a new
  // order starts its own way round.
  const [reverse, setReverse] = useState(false);
  const setSort = (next: Sort) => {
    setSortKey(next);
    setReverse(false);
  };
  const pressHead = (next: Sort) => {
    if (next === sort) setReverse((r) => !r);
    else setSort(next);
  };
  // TWO PRESSES TO DRAFT: Draft, then Confirm, in the same place. A pick
  // cannot be taken back by its manager, so the second press is where a
  // slip is caught, and it is where the first one was (B-322).
  const [confirm, setConfirm] = useState<string | null>(null);
  useEffect(() => {
    if (confirm === null) return;
    const t = setTimeout(() => setConfirm(null), 4_000);
    return () => clearTimeout(t);
  }, [confirm]);
  useEffect(() => setConfirm(null), [pickingFor?.teamId, view.current?.overall]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = view.available.filter(
      (r) =>
        (pos === 'all' || r.player.positions.includes(pos)) &&
        (needle === '' ||
          r.player.name.toLowerCase().includes(needle) ||
          clubMatches(r.player.proTeam, needle)),
    );
    // Each order by its figure, its own way first, a player with none last
    // either way, and a tie in the board's order (`sortRows`).
    const byRank = [...list].sort((a, b) => a.rank - b.rank);
    if (sort === 'name') {
      const named = byRank.sort((a, b) => a.player.name.localeCompare(b.player.name));
      return reverse ? named.reverse() : named;
    }
    const figure: Record<
      Exclude<Sort, 'name'>,
      [(r: DraftPlayerRow) => number | null, SortFirst]
    > = {
      rank: [(r) => r.rank, 'low'],
      perGame: [(r) => r.last?.perGame ?? null, 'high'],
      total: [(r) => r.last?.points ?? null, 'high'],
      value: [(r) => r.value, 'high'],
      bye: [(r) => r.bye, 'low'],
    };
    const [value, first] = figure[sort];
    return sortRows(byRank, value, first, reverse);
  }, [view.available, q, pos, sort, reverse]);

  const until = picksUntilMine(view);
  // "A NEED" ONLY WHEN IT TELLS ONE PLAYER FROM ANOTHER: at the start every
  // starting slot is open, and a mark on every row marks nothing.
  const needs = new Set(view.myNeeds);
  const sayNeed = positions.some((p) => !needs.has(p));
  // THE LINE WHERE MY NEXT PICK FALLS, counted down the board's own order:
  // only in that order, and only over every position, where the count means
  // what it says.
  const divideAfter =
    sort === 'rank' && !reverse && pos === 'all' && q.trim() === '' && until !== null && until > 0
      ? until
      : null;
  const tiers = pos !== 'all' && sort === 'rank' && !reverse;
  /** A head that sorts the list (B-377). */
  const head = (key: Sort, label: string, tip: string, first: SortFirst = 'high') => (
    <SortHead
      label={label}
      tip={tip}
      on={sort === key}
      reverse={reverse}
      first={first}
      onSort={() => pressHead(key)}
    />
  );
  const lastSeason = view.available.find((r) => r.last !== null)?.last?.season ?? null;

  const shown = rows.slice(0, SHOWN);
  // WHAT THE RANK IS, said under the list at every width: a ranking with no
  // stated basis is one people stop trusting (D4).
  const basis = view.basis ? (
    <div className="fsp-dr-note">
      <Text tone="secondary" size="xs" block>
        Rank: {view.basis}
      </Text>
    </div>
  ) : null;
  const filters = (
    <div className="fsp-dr-filters">
      <TextField
        label="Search players"
        hideLabel
        placeholder="Search players or a team"
        icon="search"
        value={q}
        onChange={setQ}
      />
      <Line gap={2} wrap>
        <div className="fsp-dr-chips">
          <Chips
            label="Position"
            single
            options={[
              { value: 'all', label: 'All' },
              ...positions.map((p) => ({ value: p, label: positionLabel(p) })),
            ]}
            value={[pos]}
            onChange={(v) => setPos(v[0] ?? 'all')}
          />
        </div>
        {detail !== 'simple' ? (
          <Segments
            label="Order"
            value={sort}
            onChange={setSort}
            items={[
              { id: 'rank', label: 'Rank' },
              { id: 'perGame', label: 'Per game' },
              ...(detail === 'pro' ? [{ id: 'total' as const, label: 'Total' }] : []),
              { id: 'name', label: 'Name' },
            ]}
          />
        ) : null}
      </Line>
    </div>
  );

  // A PLAYER UP FOR BIDS is said so, and cannot be put up again.
  const upNow = new Set(view.auction?.lots.map((l) => l.player.id) ?? []);
  const action = (r: DraftPlayerRow) =>
    pickingFor === null || upNow.has(r.player.id) ? null : nominate ? (
      <Button
        size="xs"
        variant="soft"
        label={
          pickingFor.mine
            ? `Nominate ${r.player.name}`
            : `Nominate ${r.player.name} for ${pickingFor.name}`
        }
        onClick={() => onPick(r.player.id)}
      >
        Nominate
      </Button>
    ) : confirm === r.player.id ? (
      <Button size="xs" variant="primary" onClick={() => onPick(r.player.id)}>
        Confirm
      </Button>
    ) : (
      <Button
        size="xs"
        variant="soft"
        label={
          pickingFor.mine
            ? `Draft ${r.player.name}`
            : `Draft ${r.player.name} for ${pickingFor.name}`
        }
        onClick={() => setConfirm(r.player.id)}
      >
        {pickingFor.mine ? 'Draft' : 'Pick'}
      </Button>
    );
  const star = (r: DraftPlayerRow) =>
    view.myTeamId === null || view.status === 'complete' ? null : (
      <IconButton
        icon={r.queued ? 'favorited' : 'favorite'}
        variant="quiet"
        size="sm"
        label={r.queued ? `Take ${r.player.name} out of my queue` : `Queue ${r.player.name}`}
        onClick={() => onToggleQueue(r.player.id)}
      />
    );

  const divider = (key: string) => (
    <div key={key} className="fsp-dr-divider" role="separator">
      <span>
        Your next pick · {until} {until === 1 ? 'pick' : 'picks'} away
      </span>
    </div>
  );

  if (tier === 'phone') {
    const body: ReactNode[] = [];
    shown.forEach((r, i) => {
      if (tiers && (i === 0 || shown[i - 1]!.tier !== r.tier)) {
        body.push(
          <div key={`t${r.tier}`} className="fsp-dr-tierhead">
            Tier {r.tier}
          </div>,
        );
      }
      body.push(
        <div
          key={r.player.id}
          className="fsp-row fsp-dr-prow"
          data-fills={(sayNeed && r.fillsNeed) || undefined}
          data-confirm={confirm === r.player.id || undefined}
        >
          <span className="fsp-num fsp-muted fsp-dr-rank">{r.rank}</span>
          <PositionPill pos={r.player.positions[0] ?? '—'} size="sm" />
          <div className="fsp-ui-grow fsp-dr-who">
            <div className="fsp-name">
              <PlayerName player={r.player} className="fsp-name-text" />
              <StatusBadge player={r.player} />
            </div>
            <div className="fsp-sub">
              {[
                r.player.proTeam === null ? null : clubLabel(r.player.proTeam),
                detail !== 'simple' && r.positionRank !== null
                  ? `${r.player.positions[0] ?? ''}${String(r.positionRank)}`
                  : null,
                detail !== 'simple' && r.bye !== null ? `bye ${String(r.bye)}` : null,
                detail !== 'simple' && r.last !== null ? `${one(r.last.perGame)} a game` : null,
                detail !== 'simple' && r.value !== null ? `value $${String(r.value)}` : null,
                upNow.has(r.player.id) ? 'up for bids' : null,
                sayNeed && r.fillsNeed ? 'a need' : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          {star(r)}
          {action(r)}
        </div>,
      );
      if (divideAfter !== null && i === divideAfter - 1) body.push(divider('div'));
    });
    return (
      <div className="fsp-dr-players">
        {filters}
        <div className="fsp-dr-scroll">
          {shown.length === 0 ? (
            <EmptyState>Nobody left matches that.</EmptyState>
          ) : (
            <div className="fsp-dr-list">{body}</div>
          )}
          {rows.length > SHOWN ? (
            <Text tone="secondary" size="xs" block center>
              {rows.length - SHOWN} more; search to find one.
            </Text>
          ) : null}
          {basis}
        </div>
      </div>
    );
  }

  // A TABLET'S PANE HAS ROOM FOR FEWER COLUMNS: the bye goes under the name
  // and the tier to the Tiers pane, so Draft is never off the edge.
  const compact = tier === 'tablet';
  const showBye = detail !== 'simple' && !compact;
  const showTier = detail !== 'simple' && !compact;
  const showTotals = detail === 'pro' && !compact;
  // AN AUCTION'S VALUE, in its money: a number, not advice (AUCTION.md §7).
  const showValue = detail !== 'simple' && view.auction !== null;
  const cols =
    3 +
    (showValue ? 1 : 0) +
    (detail !== 'simple' ? 1 : 0) +
    (showBye ? 1 : 0) +
    (showTier ? 1 : 0) +
    (showTotals ? 2 : 0) +
    1;
  return (
    <div className="fsp-dr-players">
      {filters}
      <div className="fsp-dr-scroll">
        {shown.length === 0 ? (
          <EmptyState>Nobody left matches that.</EmptyState>
        ) : (
          <TableScroll className="fsp-dr-tablebox">
            <table className="fsp-dr-table" aria-label="Players left">
              <thead>
                <tr>
                  {/* EVERY FIGURE'S HEAD SORTS BY IT (B-377), as the Order
                      control does; a second press turns it round. */}
                  <th scope="col" className="fsp-dr-num">
                    {head('rank', 'Rk', 'The board’s rank, by the house’s numbers.', 'low')}
                  </th>
                  <th scope="col">{head('name', 'Player', 'His name.', 'low')}</th>
                  {showValue ? (
                    <th scope="col" className="fsp-dr-num">
                      {head(
                        'value',
                        'Value',
                        'What he is worth in this league’s money, by the house’s numbers.',
                      )}
                    </th>
                  ) : null}
                  {showBye ? (
                    <th scope="col" className="fsp-dr-num">
                      {head('bye', 'Bye', 'The week his team does not play.', 'low')}
                    </th>
                  ) : null}
                  {detail !== 'simple' ? (
                    <>
                      <th scope="col" className="fsp-dr-num">
                        {head(
                          'perGame',
                          lastSeason === null ? 'Per game' : `${String(lastSeason)}/gm`,
                          lastSeason === null
                            ? 'Points a game last season.'
                            : `Points a game in ${String(lastSeason)}, in this league’s scoring.`,
                        )}
                      </th>
                    </>
                  ) : null}
                  {showTotals ? (
                    <>
                      <th scope="col" className="fsp-dr-num">
                        <Term tip="Games played that season.">GP</Term>
                      </th>
                      <th scope="col" className="fsp-dr-num">
                        {head('total', 'Pts', 'Points that season, in this league’s scoring.')}
                      </th>
                    </>
                  ) : null}
                  {showTier ? (
                    <th scope="col" className="fsp-dr-num">
                      <Term tip="His tier at his position: players a manager might take for one another.">
                        Tier
                      </Term>
                    </th>
                  ) : null}
                  <th scope="col">
                    <span className="fsp-sr">Queue</span>
                  </th>
                  <th scope="col">
                    <span className="fsp-sr">{nominate ? 'Nominate' : 'Draft'}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.flatMap((r, i) => {
                  const out: ReactNode[] = [];
                  if (tiers && (i === 0 || shown[i - 1]!.tier !== r.tier)) {
                    out.push(
                      <tr key={`t${r.tier}`} className="fsp-dr-tierrow">
                        <td colSpan={cols}>Tier {r.tier}</td>
                      </tr>,
                    );
                  }
                  out.push(
                    <tr
                      key={r.player.id}
                      data-fills={(sayNeed && r.fillsNeed) || undefined}
                      data-confirm={confirm === r.player.id || undefined}
                    >
                      <td className="fsp-dr-num fsp-muted">{r.rank}</td>
                      <td>
                        <div className="fsp-dr-player">
                          <PositionPill pos={r.player.positions[0] ?? '—'} size="sm" />
                          <div className="fsp-dr-who">
                            <div className="fsp-name">
                              <PlayerName player={r.player} className="fsp-name-text" />
                              <StatusBadge player={r.player} />
                            </div>
                            <div className="fsp-sub">
                              {[
                                r.player.proTeam === null ? null : clubLabel(r.player.proTeam),
                                detail !== 'simple' && r.positionRank !== null
                                  ? `${r.player.positions[0] ?? ''}${String(r.positionRank)}`
                                  : null,
                                compact && detail !== 'simple' && r.bye !== null
                                  ? `bye ${String(r.bye)}`
                                  : null,
                                upNow.has(r.player.id) ? 'up for bids' : null,
                                sayNeed && r.fillsNeed ? 'a need' : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </div>
                        </div>
                      </td>
                      {showValue ? (
                        <td className="fsp-dr-num fsp-dr-strong">
                          {r.value === null ? '—' : `$${String(r.value)}`}
                        </td>
                      ) : null}
                      {showBye ? <td className="fsp-dr-num">{r.bye ?? '—'}</td> : null}
                      {detail !== 'simple' ? (
                        <td className="fsp-dr-num fsp-dr-strong">
                          {r.last === null ? '—' : one(r.last.perGame)}
                        </td>
                      ) : null}
                      {showTotals ? (
                        <>
                          <td className="fsp-dr-num">{r.last?.games ?? '—'}</td>
                          <td className="fsp-dr-num">
                            {r.last === null ? '—' : one(r.last.points)}
                          </td>
                        </>
                      ) : null}
                      {showTier ? <td className="fsp-dr-num">{r.tier}</td> : null}
                      <td className="fsp-dr-act">{star(r)}</td>
                      <td className="fsp-dr-act">{action(r)}</td>
                    </tr>,
                  );
                  if (divideAfter !== null && i === divideAfter - 1) {
                    out.push(
                      <tr key="divider" className="fsp-dr-dividerrow" role="separator">
                        <td colSpan={cols}>
                          Your next pick · {until} {until === 1 ? 'pick' : 'picks'} away
                        </td>
                      </tr>,
                    );
                  }
                  return out;
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
        {rows.length > SHOWN ? (
          <Text tone="secondary" size="xs" block center>
            {rows.length - SHOWN} more; search to find one.
          </Text>
        ) : null}
        {basis}
      </div>
    </div>
  );
}

// ── The board ───────────────────────────────────────────────────────────────

/** What the board draws: a room's view, or a kept mock's seats and picks. */
export type BoardOf = Pick<DraftView, 'seats' | 'rounds' | 'board' | 'current' | 'myTeamId'>;

/**
 * Every pick by round and seat. `settled` is a draft that is over and was never
 * a room anybody sat in — a kept mock — so a seat says only whether a bot
 * drafted it, never who is here or away.
 */
export function BoardPane({
  view,
  settled = false,
  auction = false,
}: {
  view: BoardOf;
  settled?: boolean;
  /** An auction's board: each team's players in the order bought, with the price (AUCTION.md §11). */
  auction?: boolean;
}) {
  const seats = [...view.seats].sort((a, b) => a.slot - b.slot);
  const rounds = Array.from({ length: view.rounds }, (_, i) => i + 1);
  const byRound = useMemo(() => {
    const out = new Map<string, DraftView['board'][number]>();
    // EACH CELL IN ITS SLOT'S COLUMN, whoever a trade gave the pick to.
    for (const c of view.board) out.set(`${String(c.round)}:${c.originalTeamId ?? c.teamId}`, c);
    return out;
  }, [view.board]);
  const box = useRef<HTMLDivElement>(null);
  const at = view.current?.overall ?? null;
  // THE PICK ON THE CLOCK IN VIEW, inside the board's own box only, so the
  // board follows the draft without moving anything around it.
  useEffect(() => {
    const el = box.current?.querySelector<HTMLElement>('[data-current="true"]');
    const scroller = box.current;
    if (!el || !scroller) return;
    const cell = el.getBoundingClientRect();
    const frame = scroller.getBoundingClientRect();
    scroller.scrollBy({
      left: cell.left - frame.left - (frame.width - cell.width) / 2,
      top: cell.top - frame.top - (frame.height - cell.height) / 2,
      behavior: 'smooth',
    });
  }, [at]);

  return (
    <div className="fsp-dr-boardbox" ref={box}>
      <Colored
        className="fsp-draft-board fsp-dr-board"
        colors={{ '--fsp-seats': String(seats.length) }}
      >
        <div className="fsp-draft-board-head fsp-dr-corner" aria-hidden>
          {auction ? '#' : 'Rd'}
        </div>
        {seats.map((s) => (
          // THE WHOLE HEAD OPENS THE SEAT'S DRAFT SO FAR (B-338), never the
          // league's team page: that would leave the room, and a mock's seat
          // is its bot's. The whole head, so a finger has a target.
          <SeatButton
            key={s.team.id}
            seat={s}
            className="fsp-draft-board-head fsp-dr-head"
            data-mine={s.team.isMine || undefined}
          >
            <TeamMark team={s.team} size={18} />
            <span className="fsp-dr-headtext">
              <span className="fsp-dr-headname">
                {s.team.isMine ? 'You' : s.team.ownerName || s.team.abbreviation}
              </span>
              <span className="fsp-dr-headsub">
                {s.ai ? 'bot' : settled ? '' : s.autopilot ? 'auto' : s.connected ? 'here' : 'away'}
              </span>
            </span>
          </SeatButton>
        ))}
        {rounds.map((round) => (
          <BoardRound
            key={round}
            round={round}
            seats={seats}
            cells={byRound}
            view={view}
            auction={auction}
          />
        ))}
      </Colored>
    </div>
  );
}

function BoardRound({
  round,
  seats,
  cells,
  view,
  auction,
}: {
  round: number;
  seats: DraftView['seats'];
  cells: Map<string, DraftView['board'][number]>;
  view: BoardOf;
  auction: boolean;
}) {
  return (
    <>
      <div className="fsp-draft-round">{round}</div>
      {seats.map((s) => {
        const c = cells.get(`${String(round)}:${s.team.id}`);
        if (!c) return <div key={s.team.id} className="fsp-draft-cell" />;
        const pos = c.player?.positions[0]?.toLowerCase();
        const how = byWord(c.by);
        // A TRADE, SAID SMALL IN THE CELL (B-346): whose a traded pick is now,
        // who made it with one, and who has a player traded since.
        const who = (teamId: string) =>
          teamId === view.myTeamId
            ? 'you'
            : (view.seats.find((x) => x.team.id === teamId)?.team.ownerName ?? '');
        const traded = [
          c.player !== null && c.originalTeamId !== null ? `by ${who(c.teamId)}` : '',
          c.player !== null && c.heldBy !== null ? `now ${who(c.heldBy)}` : '',
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <Colored
            key={s.team.id}
            className="fsp-draft-cell fsp-dr-cell"
            data-filled={c.player ? 'true' : undefined}
            data-current={c.isCurrent ? 'true' : undefined}
            data-mine={(c.heldBy ?? c.teamId) === view.myTeamId ? 'true' : undefined}
            data-traded={c.originalTeamId !== null || c.heldBy !== null ? 'true' : undefined}
            colors={pos ? { '--fsp-pos': `var(--fsp-pos-${pos})` } : {}}
          >
            {c.player ? (
              <>
                <PlayerName player={c.player} className="fsp-draft-cell-name">
                  {c.player.shortName}
                </PlayerName>
                <span className="fsp-draft-cell-sub">
                  {positionLabel(c.player.positions[0] ?? '')} ·{' '}
                  {c.player.proTeam === null ? 'FA' : clubLabel(c.player.proTeam)}
                  {c.price !== null
                    ? ` · $${String(c.price)}`
                    : traded
                      ? ` · ${traded}`
                      : how
                        ? ` · ${how}`
                        : ''}
                </span>
              </>
            ) : auction ? (
              // AN EMPTY SPOT IN AN AUCTION is nobody's pick: it fills when its team buys.
              <span className="fsp-draft-cell-sub" />
            ) : (
              <span className="fsp-draft-cell-sub">
                {pickLabel(c.round, c.slot)}
                {c.originalTeamId !== null
                  ? ` · ${c.teamId === view.myTeamId ? 'yours' : `${who(c.teamId)}’s`}`
                  : ''}
                {c.isCurrent ? ' · on the clock' : ''}
              </span>
            )}
          </Colored>
        );
      })}
    </>
  );
}

// ── Every team's positions ──────────────────────────────────────────────────

export function TeamsPane({ view, positions }: { view: DraftView; positions: readonly string[] }) {
  const counts = positionCounts(view);
  const beforeList = teamsBeforeMine(view);
  const before = new Set(beforeList);
  const until = picksUntilMine(view);
  // IN THE ORDER THEY PICK FROM NOW, so the ones between me and my next pick
  // are at the top, where the eye starts.
  const from = view.current?.overall ?? 1;
  const upcoming = view.board.filter((c) => c.overall >= from && c.player === null);
  const order: string[] = [];
  for (const c of upcoming) if (!order.includes(c.teamId)) order.push(c.teamId);
  for (const s of view.seats) if (!order.includes(s.team.id)) order.push(s.team.id);
  const seat = new Map(view.seats.map((s) => [s.team.id, s]));
  return (
    <div className="fsp-dr-teams">
      {until !== null && until > 0 ? (
        <Text tone="secondary" size="xs" block>
          In the order they pick from now.
        </Text>
      ) : null}
      <TableScroll className="fsp-dr-tablebox">
        <table className="fsp-dr-table fsp-dr-tracker" aria-label="What every team has drafted">
          <thead>
            <tr>
              <th scope="col">Team</th>
              {positions.map((p) => (
                <th key={p} scope="col" className="fsp-dr-num">
                  {positionLabel(p)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {order.map((teamId) => {
              const s = seat.get(teamId);
              if (!s) return null;
              const row = counts.get(teamId) ?? {};
              return (
                <tr
                  key={teamId}
                  data-mine={s.team.isMine || undefined}
                  data-before={before.has(teamId) || undefined}
                >
                  <td>
                    <span className="fsp-dr-teamcell">
                      <TeamMark team={s.team} size={16} />
                      <span className="fsp-dr-teamwho">
                        {/* THE TEAM'S SHORT NAME, which a narrow pane has room for
                            and which still says who (UX-11); the whole name is
                            the button's accessible name and a tap away. */}
                        <SeatName seat={s}>{s.team.isMine ? 'You' : s.team.abbreviation}</SeatName>
                        {before.has(teamId) ? (
                          <span className="fsp-dr-before">
                            {beforeList.filter((t) => t === teamId).length > 1
                              ? 'twice before you'
                              : 'before you'}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </td>
                  {positions.map((p) => (
                    <td key={p} className="fsp-dr-num" data-none={!row[p] || undefined}>
                      {row[p] ?? 0}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>
    </div>
  );
}

// ── Tiers left, and what the last picks took ────────────────────────────────

export function TiersPane({ view, positions }: { view: DraftView; positions: readonly string[] }) {
  const left = tiersLeft(view.available, positions);
  const run = recentRun(view, 10);
  // AN AUCTION'S SQUARES ARE NOT IN THE ORDER THINGS SOLD, so it has no run to show.
  const made = view.auction !== null ? 0 : view.board.filter((c) => c.player !== null).length;
  return (
    <div className="fsp-dr-tiers">
      <Caption>The top tier left at each position</Caption>
      {left.map((t) => (
        <div key={t.position} className="fsp-row fsp-dr-tierline">
          <PositionPill pos={t.position} size="sm" />
          <div className="fsp-ui-grow">
            <div className="fsp-name">
              Tier {t.tier} · {t.left} left
            </div>
            <div className="fsp-sub">
              {t.nextLeft === null
                ? 'No tier after it'
                : `then ${String(t.nextLeft)} in tier ${String(t.tier + 1)}`}
            </div>
          </div>
          <span className="fsp-dr-pips" aria-hidden>
            {Array.from({ length: Math.min(t.left, 8) }, (_, i) => (
              <span key={i} className="fsp-dr-pip" />
            ))}
          </span>
        </div>
      ))}
      {made > 0 ? (
        <>
          <Caption>
            {made === 1 ? 'The last pick' : `The last ${String(Math.min(10, made))} picks`}
          </Caption>
          <Line gap={2} wrap>
            {run.map((r) => (
              <span key={r.position} className="fsp-dr-runchip">
                <PositionPill pos={r.position} size="sm" />
                <Text size="sm" weight="bold" numeric>
                  {r.count}
                </Text>
              </span>
            ))}
          </Line>
        </>
      ) : null}
    </div>
  );
}

/** The picks so far, newest first: who took whom, and how. */
export function PicksPane({ view }: { view: DraftView }) {
  const seat = new Map(view.seats.map((s) => [s.team.id, s]));
  const made = view.board.filter((c) => c.player !== null).sort((a, b) => b.overall - a.overall);
  if (made.length === 0) return <EmptyState>No picks yet.</EmptyState>;
  return (
    <div>
      {made.slice(0, 40).map((c) => {
        const s = seat.get(c.teamId);
        const how = byWord(c.by);
        return (
          <div key={c.overall} className="fsp-row fsp-dr-pickrow">
            <span className="fsp-num fsp-muted">{pickLabel(c.round, c.slot)}</span>
            <PositionPill pos={c.player!.positions[0] ?? '—'} size="sm" />
            <div className="fsp-ui-grow">
              <div className="fsp-name">
                <PlayerName player={c.player!} className="fsp-name-text" club={c.player!.proTeam} />
              </div>
              <div className="fsp-sub">
                {s?.team.isMine ? 'You' : (s?.team.ownerName ?? '')}
                {how ? ` · ${how}` : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Mine: the queue and the team ────────────────────────────────────────────

export function QueuePane({
  view,
  onSetQueue,
  mock = false,
}: {
  view: DraftView;
  onSetQueue: (ids: string[]) => void;
  /** A mock's clock is the mock's, not the league's, and is said so. */
  mock?: boolean;
}) {
  const ids = view.myQueue.map((p) => p.id);
  const until = picksUntilMine(view);
  const moveBy = (i: number, d: -1 | 1) => {
    const next = [...ids];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    onSetQueue(next);
  };
  // TO THE TOP IN ONE PRESS (B-415): the player wanted next, from anywhere in
  // the queue, without a press for every place above him.
  const toTop = (i: number) => onSetQueue([ids[i]!, ...ids.filter((_, k) => k !== i)]);
  const auto = view.whenTheClockExpires === 'queue-then-rankings';
  const auction = view.auction !== null;
  return (
    <div className="fsp-dr-queue">
      {view.myQueue.length === 0 ? (
        <div className="fsp-dr-note">
          <Text tone="secondary" block>
            {until !== null && until <= 3 && until > 0
              ? 'Your queue is empty and your pick is close. Star a few players in the list.'
              : 'Star players in the list to line them up here.'}
          </Text>
        </div>
      ) : (
        view.myQueue.map((p, i) => (
          <div key={p.id} className="fsp-row fsp-dr-qrow">
            <span className="fsp-num fsp-muted">{i + 1}</span>
            <PositionPill pos={p.positions[0] ?? '—'} size="sm" />
            <div className="fsp-name fsp-ui-grow">
              <PlayerName player={p} className="fsp-name-text" club={p.proTeam} />
            </div>
            <IconButton
              icon="top"
              variant="quiet"
              size="sm"
              label={`Move ${p.name} to the top`}
              disabled={i === 0}
              onClick={() => toTop(i)}
            />
            <IconButton
              icon="up"
              variant="quiet"
              size="sm"
              label={`Move ${p.name} up`}
              disabled={i === 0}
              onClick={() => moveBy(i, -1)}
            />
            <IconButton
              icon="down"
              variant="quiet"
              size="sm"
              label={`Move ${p.name} down`}
              disabled={i === view.myQueue.length - 1}
              onClick={() => moveBy(i, 1)}
            />
            <IconButton
              icon="close"
              variant="quiet"
              size="sm"
              label={`Take ${p.name} out of my queue`}
              onClick={() => onSetQueue(ids.filter((x) => x !== p.id))}
            />
          </div>
        ))
      )}
      <div className="fsp-dr-note">
        <Text tone="secondary" size="xs" block>
          {/* AUTO ALWAYS TAKES THE QUEUE FIRST; what a clock at zero takes is
              the league's rule, and the two are said apart where they differ. */}
          {auction
            ? view.whenTheClockExpires === 'off'
              ? 'This league waits for your nomination. With auto on, the room puts up the top of this queue, else the highest-valued player.'
              : 'When your nomination clock runs out, or auto is on, the room puts up the top of this queue at the lowest bid, else the highest-valued player.'
            : auto
              ? 'When your clock runs out, or auto is on, the room takes the top of this queue, then your priorities, then the board.'
              : view.whenTheClockExpires === 'rankings'
                ? 'When your clock runs out, the room takes from your priorities, then the board. With auto on, it takes the top of this queue first.'
                : mock
                  ? 'This mock waits for your pick. With auto on, the room takes the top of this queue, then your priorities, then the board.'
                  : 'This league’s clock waits for you at zero. With auto on, the room takes the top of this queue, then your priorities, then the board.'}
        </Text>
      </div>
    </div>
  );
}

export function MyTeamPane({ view }: { view: DraftView }) {
  const byes = sharedByes(view);
  if (view.myTeamId === null) {
    return <EmptyState>You do not have a seat in this draft.</EmptyState>;
  }
  // WHAT I PAID FOR EACH, in an auction (AUCTION.md §10).
  const paid = new Map(
    view.board.flatMap((c) =>
      c.player !== null && c.price !== null ? [[c.player.id, c.price] as const] : [],
    ),
  );
  const mine = view.auction?.budgets.find((b) => b.teamId === view.myTeamId) ?? null;
  return (
    <div className="fsp-dr-mine">
      <div className="fsp-dr-note">
        {mine !== null ? (
          <Text size="sm" weight="bold" block>
            ${mine.left} left
            {mine.spotsLeft > 0 ? ` · most $${String(mine.most)}` : ''} · {mine.spotsLeft}{' '}
            {mine.spotsLeft === 1 ? 'spot' : 'spots'} to fill
          </Text>
        ) : null}
        <Text size="sm" block>
          {view.myNeeds.length
            ? `Still to fill: ${view.myNeeds.map(positionLabel).join(', ')}`
            : 'Every starting slot is filled.'}
        </Text>
        {byes.map((b) => (
          <Text key={b.week} tone="secondary" size="xs" block>
            Bye {b.week}: {b.names.join(', ')}
          </Text>
        ))}
      </div>
      {view.myRoster.map((r, i) => (
        <div
          key={`${r.slot}-${String(i)}`}
          className="fsp-row fsp-dr-slotrow"
          data-empty={r.player === null || undefined}
        >
          <PositionPill pos={r.slot} size="sm" />
          {r.player ? (
            <div className="fsp-ui-grow">
              <div className="fsp-name">
                <PlayerName player={r.player} className="fsp-name-text" />
              </div>
              <div className="fsp-sub">
                {positionLabel(r.player.positions[0] ?? '')} ·{' '}
                {r.player.proTeam === null ? 'FA' : clubLabel(r.player.proTeam)}
                {r.player.proTeam && view.byeWeeks[r.player.proTeam] !== undefined
                  ? ` · bye ${String(view.byeWeeks[r.player.proTeam])}`
                  : ''}
                {paid.has(r.player.id) ? ` · $${String(paid.get(r.player.id))}` : ''}
              </div>
            </div>
          ) : (
            <Text tone="muted" size="sm">
              {r.slotName}
            </Text>
          )}
        </div>
      ))}
    </div>
  );
}

/** Who sits where before the start, and who is here. */
export function SeatsPane({
  view,
  seatActions,
}: {
  view: DraftView;
  /** A league mock's lobby: every seat is open to take (MOCK_DRAFT.md §6.2). */
  seatActions: { take: (teamId: string) => void; leave: () => void } | null;
}) {
  const seats = [...view.seats].sort((a, b) => a.slot - b.slot);
  const seated = view.myTeamId !== null;
  const mockLobby = view.mockRoom !== null && view.status === 'lobby';
  return (
    <div>
      {seats.map((s) => {
        const open = s.team.ownerName === OPEN_SEAT && s.ai === null;
        return (
          <div key={s.team.id} className="fsp-row fsp-dr-seatrow">
            <span className="fsp-num fsp-muted">{s.slot}</span>
            <TeamMark team={s.team} size={24} />
            <div className="fsp-ui-grow">
              <div className="fsp-name">
                <SeatName seat={s} />
              </div>
              <div className="fsp-sub">
                {s.ai
                  ? `${s.ai.name}, a bot · ${s.ai.difficulty}`
                  : open
                    ? `Open: a bot takes it when the ${mockLobby ? 'mock' : 'draft'} starts`
                    : s.team.ownerName}
              </div>
              {/* HOW IT DRAFTS, its first three phrases; the seat's card has
                  them all (AI_MANAGERS.md §4.7). */}
              {s.ai && s.ai.style.length > 0 ? (
                <div className="fsp-sub fsp-ui-wrap-text">{s.ai.style.slice(0, 3).join(' · ')}</div>
              ) : null}
            </div>
            {seatActions !== null && s.team.isMine ? (
              <Button
                size="xs"
                variant="quiet"
                onClick={seatActions.leave}
                label={`Leave seat ${String(s.slot)}`}
              >
                Leave
              </Button>
            ) : seatActions !== null && open ? (
              <Button
                size="xs"
                variant={seated ? 'secondary' : 'primary'}
                onClick={() => seatActions.take(s.team.id)}
                label={`${seated ? 'Move to' : 'Take'} seat ${String(s.slot)}`}
              >
                {seated ? 'Move' : 'Take'}
              </Button>
            ) : s.team.isMine ? (
              <Chip tone="good">You</Chip>
            ) : s.ai ? (
              <Chip tone="accent">Bot</Chip>
            ) : open ? (
              <Chip>Open</Chip>
            ) : s.connected ? (
              <Chip tone="good">Here</Chip>
            ) : (
              <Chip>Not here yet</Chip>
            )}
          </div>
        );
      })}
    </div>
  );
}
